import { type ECharts } from 'echarts';
import Big from './bigNumber';
import { util } from 'echarts/core';

export const BUILTIN_OBJECT = {
  '[object Function]': true,
  '[object RegExp]': true,
  '[object Date]': true,
  '[object Error]': true,
  '[object CanvasGradient]': true,
  '[object CanvasPattern]': true,
  // For node-canvas
  '[object Image]': true,
  '[object Canvas]': true
};

export function normalizeToArray<T>(value?: T | T[]): T[] {
  if (value instanceof Array) {
    return value;
  }
  if (value == null || undefined) {
    return [];
  }
  return [value];
}

export function isObject<T = unknown>(value: T): value is object & T {
  const type = typeof value;
  return type === 'function' || (!!value && type === 'object');
}

export function isEffectObject(value: unknown): boolean {
  if (!isObject(value)) {
    return false;
  }
  if (Object.keys(value).length === 0) {
    return false;
  }
  return true;
}

export function isStateContainEffectStyle(state: unknown): boolean {
  if (!isObject(state)) {
    return false;
  }
  if (!isEffectObject((state as any).style)) {
    return false;
  }
  return true;
}

export function isArray(value: unknown): value is unknown[] {
  if (Array.isArray) {
    return Array.isArray(value);
  }
  return Object.prototype.toString.call(value) === '[object Array]';
}

export function isBuiltInObject(value: unknown): boolean {
  return !!BUILTIN_OBJECT[Object.prototype.toString.call(value)];
}

export function getRange(arr: Array<number>): Array<number> {
  if (arr.length === 0) {
    return [];
  }
  if (arr.length === 1) {
    return [arr[0], arr[0]];
  }
  const range = [arr[0], arr[0]];
  arr.forEach(item => {
    if (item < range[0]) {
      range[0] = item;
    }
    if (item > range[1]) {
      range[1] = item;
    }
  });
  return range;
}

export function getURLParam(name: string): string | null {
  const TWO = 2;
  // eslint-disable-next-line no-restricted-globals
  const matches = new RegExp(`[?|&]${name}=([^&;]+?)(&|#|;|$)`, 'ig').exec(location.search);
  if (!matches || matches.length < TWO || !matches[1]) {
    return null;
  }
  return matches[1].replace(/\+/g, '%20');
}

export function measureTextWidth(
  text: string,
  fontSize: number,
  fontFamily = '',
  fontWeight: string | number = ''
) {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
  return ctx.measureText(text).width;
}

export function getComponentModelsByName(ecIns: ECharts, name: string) {
  let count = 0;
  const result = [];
  while (true) {
    const modelItem = ecIns.getModel().getComponent(name, count);
    if (modelItem) {
      result.push(modelItem);
      count++;
    } else {
      break;
    }
  }
  return result;
}

export function getComponentViewsByModels(ecIns: ECharts, model) {
  const models = Array.isArray(model) ? model : [model];
  return models.map(item => ecIns.getViewOfComponentModel(item));
}

/**
 * 比较两个数组内容是否一致
 * @param arr1
 * @param arr2
 * @returns
 */
export function equalsArray(arr1: Array<unknown>, arr2: Array<unknown>): boolean {
  if (!arr1 || !arr2) {
    return false;
  }

  if (arr1.length !== arr2.length) {
    return false;
  }

  const len = arr1.length;

  for (let i = 0; i < len; i++) {
    if (isArray(arr1[i]) && isArray(arr2[i])) {
      if (!equalsArray(arr1[i] as unknown[], arr2[i] as unknown[])) {
        return false;
      }
    } else if (arr1[i] !== arr2[i]) {
      return false;
    } else {
      //
    }
  }
  return true;
}

// 判断是否为移动端
export function isMobile(): boolean {
  return /Android|webOS|iPhone|iPod|BlackBerry/i.test(navigator.userAgent);
}

// 求和
export function summation(params: Array<number>): number {
  let sum = 0;
  params.forEach((item: number) => {
    sum += item;
  });
  return sum;
}

/**
 * 加法运算，避免数据相加小数点后产生多位数和计算精度损失。
 *
 * @param num1加数1 | num2加数2
 */
export function numAdd(num1, num2) {
  const bigA = new Big(num1.toString());
  const bigB = new Big(num2.toString());
  const sum = bigA.plus(bigB);
  return sum.toString();
}
/**
 * 减法运算，避免数据相减小数点后产生多位数和计算精度损失。
 *
 * @param num1被减数  |  num2减数
 */
export function numSub(num1, num2) {
  const bigA = new Big(num1.toString());
  const bigB = new Big(num2.toString());
  const difference = bigA.minus(bigB);
  return difference.toString(); // 返回字符串形式的结果，保留精度
}

/**
 * 统计对象中相同属性的属性值出现的次数
 * @param opts 源数据
 * @param name 筛选属性
 * @returns
 */
export function handleElementNumber(opts, name) {
  return opts.reduce((map, obj) => {
    if (obj[name] in map) {
      map[obj[name]]++;
    } else {
      map[obj[name]] = 1;
    }
    return map;
  }, {});
}
/**
 * 排序
 * @param params 排序属性
 * @returns
 */
export function arrayObjectSort(params) {
  return function (a, b) {
    const value1 = a[params];
    const value2 = b[params];
    return value1 - value2;
  };
}

export function merge(obj1, obj2, overwrite = true) {
  return util.merge(obj1, obj2, overwrite);
}
